import { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';

import { StopPropagation } from '../../utils/events';
import RichText from '../RichText';
import CtaButtonLink from '../CtaButtonLink';
import CtaTextLink from '../CtaTextLink';
import Portal from '../Portal';

import {
  ModalBackground,
  ModalContainer,
  ModalClose,
  Title,
  Description,
  WrapperCtas,
} from './styles';

const ModalWindow = ({
  active,
  onClose,
  elementRef,
  data: { closeButtonLabel, content },
  themes,
}) => {
  const [defaultPageScroll, setDefaultPageScroll] = useState(null);

  // Enable or disable page scrolling based on the active state
  useEffect(() => setPageScrolling(!active), [active]);

  // Reenable page scrolling on unmount
  useEffect(() => setPageScrolling(true), []);

  const setPageScrolling = (scrollable) => {
    if (typeof document !== 'undefined') {
      setDefaultPageScroll(
        scrollable ? null : document.documentElement.style.overflow
      );
      const overflow = scrollable ? defaultPageScroll : 'hidden';
      document.documentElement.style.overflow = overflow;
    }
  };

  return (
    <Portal>
      <FocusLock disabled={!active} returnFocus>
        <ModalBackground
          className={active ? 'is-active' : ''}
          onClick={onClose}
        >
          <ModalContainer
            className={active ? 'is-active' : ''}
            onClick={StopPropagation}
          >
            <ModalClose
              onClick={() => {
                onClose();
                // Set short timeout to enable browser to add focus back to original trigger of modals
                if (elementRef.current) {
                  setTimeout(() => {
                    elementRef.current.focus();
                  }, 20);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  onClose();
                  // Set short timeout to enable browser to add focus back to original trigger of modals
                  if (elementRef.current) {
                    setTimeout(() => {
                      elementRef.current.focus();
                    }, 20);
                  }
                }
              }}
            >
              <span>{closeButtonLabel}</span>
              <div>
                <svg viewBox="0 0 10 10">
                  <path d="M0,0 L10,10 M10,0 L0,10" />
                </svg>
              </div>
            </ModalClose>
            {content &&
              content.map(({ id: key, headingRTF, paragraphRTF, ctaLinks }) => (
                <Fragment key={key}>
                  {headingRTF && (
                    <Title>
                      <RichText document={headingRTF} />
                    </Title>
                  )}
                  {paragraphRTF && (
                    <Description>
                      <RichText document={paragraphRTF} />
                    </Description>
                  )}
                  <WrapperCtas>
                    {ctaLinks &&
                      ctaLinks.map((link) => {
                        const {
                          linkType,
                          style,
                          linkUrlMarkdown,
                          linkTextMarkdown,
                          linkText,
                          ariaLabel,
                          id,
                        } = link;
                        if (linkType === 'Button' || linkType === 'button') {
                          return (
                            <CtaButtonLink
                              key={id}
                              contentId={id}
                              variant={style}
                              url={linkUrlMarkdown.linkUrlMarkdown}
                              ariaLabel={ariaLabel}
                            >
                              {linkText || linkTextMarkdown.linkTextMarkdown}
                            </CtaButtonLink>
                          );
                        } else if (linkType === 'Text-Link') {
                          return (
                            <CtaTextLink
                              key={id}
                              contentId={id}
                              variant={style}
                              url={linkUrlMarkdown.linkUrlMarkdown}
                              ariaLabel={ariaLabel}
                              themes={themes}
                            >
                              {linkText || linkTextMarkdown.linkTextMarkdown}
                            </CtaTextLink>
                          );
                        }
                        return null;
                      })}
                  </WrapperCtas>
                </Fragment>
              ))}
          </ModalContainer>
        </ModalBackground>
      </FocusLock>
    </Portal>
  );
};

ModalWindow.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape().isRequired,
  elementRef: PropTypes.objectOf(PropTypes.shape),
  themes: PropTypes.string.isRequired,
};

ModalWindow.defaultProps = {
  elementRef: {},
};

export default ModalWindow;
